import { Action, Dispatch } from 'redux';

import pLimit from 'p-limit';
import { IAppState } from '../reducers';

import {
  IOrg,
  IUser,
  getOrgs,
  getUsersByOrg,
  getUserOrgs,
} from '../../api/idm';

export enum ActionType {
  ClearImpersonation = 'Impersonation.ClearImpersonation',
  UserSet = 'Impersonation.UserSet',
  StartLoadOrgs = 'Impersonation.StartLoadOrgs',
  EndLoadOrgs = 'Impersonation.EndLoadOrgs',
  ClearOrgUsers = 'Impersonation.ClearOrgUsers',
  StartLoadOrgUsers = 'Impersonation.StartLoadOrgUsers',
  EndLoadOrgUsers = 'Impersonation.EndLoadOrgUsers',
  EndLoadOrgUserRoles = 'Impersonation.EndLoadOrgUserRoles',
}

export interface IImpersonationAction extends Action<ActionType> {
  payload: any
}

export interface IOrgLoadState {
  loading: boolean;
  orgs?: IOrg[];
  error?: Error;
}

export interface IUserLoadState {
  loading: boolean;
  users?: IUser[];
  error?: Error;
  /**
   * Role name per user id, for the selected org only. Absent until the follow-up lookup
   * returns - the list renders without it rather than waiting, because IDM has no endpoint
   * that returns an org's users and their roles together.
   */
  roles?: Record<number, string>;
}

export interface IState {
  user: IUser;
  orgLoadState: IOrgLoadState;
  userLoadState: IUserLoadState;
}

/**
 * Roles cost one request per user. Enough parallelism to keep a full org quick, low enough
 * not to fire a request per user at IDM all at once.
 */
const ORG_ROLE_LOOKUP_CONCURRENCY = 6;

const INITIAL_STATE: IState = {
  user: null,
  orgLoadState: null,
  userLoadState: null,
};

export default (state: IState = INITIAL_STATE, action: IImpersonationAction) => {
  switch (action.type) {
    case ActionType.UserSet:
    case ActionType.StartLoadOrgs:
    case ActionType.EndLoadOrgs:
    case ActionType.StartLoadOrgUsers:
    case ActionType.EndLoadOrgUsers:
      return {
        ...state,
        ...action.payload,
      };
    case ActionType.EndLoadOrgUserRoles:
      // Merged into userLoadState rather than replacing it: the roles arrive after the users
      // and must not clobber them. The thunk drops a result belonging to a superseded org, so
      // anything reaching here is for the list currently on screen.
      return {
        ...state,
        userLoadState: {
          ...state.userLoadState,
          roles: action.payload.roles,
        },
      };
    case ActionType.ClearOrgUsers:
      return {
        ...state,
        userLoadState: null,
      };
    case ActionType.ClearImpersonation:
      return {
        ...state,
        user: null,
      };
    default:
      return state;
  }
};

export const loadOrgs = () => async (dispatch: Dispatch<IImpersonationAction>, getState: () => IAppState) => {
  dispatch({
    payload: {
      orgLoadState: {
        loading: true,
      },
    },
    type: ActionType.StartLoadOrgs,
  });

  try {
    const orgs = await getOrgs();

    dispatch({
      payload: {
        orgLoadState: {
          loading: false,
          orgs: orgs.data.data,
        },
      },
      type: ActionType.EndLoadOrgs,
    });
  } catch (e) {
    dispatch({
      payload: {
        orgLoadState: {
          loading: false,
          error: e,
        },
      },
      type: ActionType.EndLoadOrgs,
    });
  }
};

/**
 * Fills in the role each user holds *in the selected org*. A user can belong to several orgs
 * with a different role in each, so the entry is matched on orgId - a role from another org
 * would be actively misleading when the point is to know what you are about to impersonate.
 */
const loadOrgUserRoles = async (
  org: IOrg,
  users: IUser[],
  dispatch: Dispatch<IImpersonationAction>,
  getState: () => IAppState,
) => {
  if (!users.length) return;

  const limit = pLimit(ORG_ROLE_LOOKUP_CONCURRENCY);
  const roles: Record<number, string> = {};

  await Promise.all(users.map((user) => limit(async () => {
    try {
      const userOrgs = await getUserOrgs(user.id);
      const match = (userOrgs.data.data || []).find((userOrg) => userOrg.orgId === org.id);
      const name = match?.role?.name;

      if (name) roles[user.id] = name;
    } catch {
      // Leave this one user without a role; the rest of the list is still worth showing.
    }
  })));

  // A different org may have been picked while these were in flight. loadOrgUsers replaces the
  // users array wholesale, so an identity check on it is enough to spot a stale result.
  if (getState().user?.impersonation?.userLoadState?.users !== users) return;

  dispatch({
    payload: { roles },
    type: ActionType.EndLoadOrgUserRoles,
  });
};

export const loadOrgUsers = (org: IOrg) => async (dispatch: Dispatch<IImpersonationAction>, getState: () => IAppState) => {
  dispatch({
    payload: {
      userLoadState: {
        loading: true,
      },
    },
    type: ActionType.StartLoadOrgUsers,
  });

  let loaded: IUser[];

  try {
    const users = await getUsersByOrg(org.id);

    loaded = users.data.data;

    dispatch({
      payload: {
        userLoadState: {
          loading: false,
          users: loaded,
        },
      },
      type: ActionType.EndLoadOrgUsers,
    });
  } catch (e) {
    dispatch({
      payload: {
        userLoadState: {
          loading: false,
          error: e,
        },
      },
      type: ActionType.EndLoadOrgUsers,
    });

    return;
  }

  // Outside the try on purpose: the list is already on screen and must stay there. Inside it,
  // anything thrown while fetching roles would land in the catch above and replace a good user
  // list with a load error. The roles are also fetched after the list rather than before it -
  // one request per user is quick for the tens of users an org has, but not something the
  // picker should block on.
  await loadOrgUserRoles(org, loaded, dispatch, getState);
};

export const setUser = (user: IUser) => async (dispatch: Dispatch<IImpersonationAction>, getState: () => IAppState) => {
  dispatch({
    payload: {
      user,
    },
    type: ActionType.UserSet,
  });
};

export const clearImpersonation = () => ({
  type: ActionType.ClearImpersonation,
});

export const clearOrgUsers = () => ({
  type: ActionType.ClearOrgUsers,
});
